import { Range } from '@attain-apps/range'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as dms from '../../dms'

export interface Node extends Partial<Pick<dms.data.Record, 'values'>> {
  modelId: dms.models.ForRecord.Id
  /**
   * This is what is displayed for this {@link Node} in a visual or textual
   * representation of the tree
   */
  label: string
  /**
   * Optional title for this {@link Node}
   */
  title?: string
  /**
   * Optional additional label for this {@link Node}
   */
  sublabel?: string
  /**
   * Optional {@link dms.data.query.Query Query} for employee specific context
   * that powers assistant and org chart experiences (skills, project history,
   * availability forecasts, etc.)
   */
  peopleInsights?: dms.data.query.Query<Node.PeopleInsights>
}
export namespace Node {
  export type Id = PartialId
  export namespace Id {
    export const isId: TypeChecker<Id> = PartialId.isPartialId.bind(PartialId)
    export const assertIsId: TypeAsserter<Id> =
      PartialId.assertIsPartialId.bind(PartialId)
    /** TODO:  */
    export function tryGet(node: Partial<Node>): Id | undefined {
      return node.values?.[dms.data.Record.Id.KEY]
    }
    /** TODO:  */
    export function get(node: Partial<Node>): Id {
      return TypeAsserter.validate(tryGet(node), TypeAsserter.assertIsString)
    }
  }

  /**
   * Employee focused metadata stored on a {@link Node}.
   */
  export interface PeopleInsights {
    /**
     * S3 file name of the employee's resume/CV, if provided.
     */
    readonly resumeFileName?: string
    /**
     * Collection of skills the employee has been associated with.
     */
    readonly skills?: readonly Skill[]
    /**
     * Project assignments (both past and current) the employee has participated
     * in. A {@link ProjectEngagement.period} carrying both a start and end date
     * represents a historical engagement, while one with only a start date
     * represents a current engagement.
     */
    readonly projectEngagements?: readonly ProjectEngagement[]
    /**
     * How many hours are available now and forecasted for the upcoming months.
     */
    readonly availability?: Availability
  }
  export namespace PeopleInsights {
    export const isPeopleInsights: TypeChecker<PeopleInsights> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object<PeopleInsights>({
          availability: TypeChecker.Factory.optional(
            Availability.isAvailability,
          ),
          projectEngagements: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(ProjectEngagement.isProjectEngagement),
          ),
          resumeFileName: TypeChecker.Factory.optional(TypeChecker.isString),
          skills: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Skill.isSkill),
          ),
        }),
      )
  }

  /**
   * A skill that has been attached to an employee.
   */
  export interface Skill {
    /**
     * Human readable skill label coming from the skill entity record.
     */
    readonly name: string
    /**
     * Whether the skill is tied to a certification.
     */
    readonly hasCertification?: boolean
  }
  export namespace Skill {
    export const isSkill: TypeChecker<Skill> = TypeChecker.Factory.object({
      hasCertification: TypeChecker.Factory.optional(TypeChecker.isBoolean),
      name: TypeChecker.isString,
    })
  }

  /**
   * Relationship between an employee and a project for a specific role.
   */
  export interface ProjectEngagement {
    /**
     * Descriptive name of the project.
     */
    readonly projectName: string
    /**
     * Period of performance. Both {@link Range.start} and {@link Range.end}
     * populated indicates a past project; an undefined {@link Range.end}
     * indicates the employee is currently engaged.
     */
    readonly period: Range<Date>
    /**
     * Title or capacity the employee fulfilled for this project.
     */
    readonly role: string
    /**
     * Optional textual summary of the employee's responsibilities or outcomes.
     */
    readonly description?: string
  }
  export namespace ProjectEngagement {
    export const isProjectEngagement: TypeChecker<ProjectEngagement> =
      TypeChecker.Factory.object<ProjectEngagement>({
        description: TypeChecker.Factory.optional(TypeChecker.isString),
        period: Range.getTypeChecker(TypeChecker.isDate),
        projectName: TypeChecker.isString,
        role: TypeChecker.isString,
      })
  }

  /**
   * Snapshot of how many hours the employee has available now and over the next
   * two months.
   */
  export interface Availability {
    /**
     * Available hours for the current month.
     */
    readonly currentMonthHours?: number
    /**
     * Forecasted available hours for the next month.
     */
    readonly nextMonthForecastHours?: number
    /**
     * Forecasted available hours two months from now.
     */
    readonly twoMonthsOutForecastHours?: number
  }
  export namespace Availability {
    export const isAvailability: TypeChecker<Availability> =
      TypeChecker.Factory.object<Availability>({
        currentMonthHours: TypeChecker.Factory.optional(TypeChecker.isNumber),
        nextMonthForecastHours: TypeChecker.Factory.optional(
          TypeChecker.isNumber,
        ),
        twoMonthsOutForecastHours: TypeChecker.Factory.optional(
          TypeChecker.isNumber,
        ),
      })
  }
}
